'use client';

/**
 * WebComparisonStripCard — 'Your New Workflow' task / old-way /
 * with-Klyde comparison table.
 *
 * Ports klyde-website ProofStrip into the cards-everywhere substrate.
 * Wide viewports render a 3-column table (headers from the payload's
 * col_labels — they carry voice, so they're content, not chrome);
 * narrow viewports stack each row as a mini-card. Content via the
 * `web_landing_workflow` strategy.
 *
 * Animation is mount-time (delay-staggered), not scroll-triggered —
 * framer-motion's useInView fails inside Canvas2Card's nested
 * overflow-y-auto. Same pattern WebBenefitsGridCard uses.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebComparisonStripContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';


interface WebComparisonStripCardProps {
  strategyAction?: string;
}

export function WebComparisonStripCard({
  strategyAction = 'web_landing_workflow',
}: WebComparisonStripCardProps = {}) {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebComparisonStripContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebComparisonStripContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load workflow comparison');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  return (
    <section className="web-surface relative py-16 px-6 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto">
        <motion.h2
          className={cn(T.name, 'text-center mb-10')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {data.headline}
        </motion.h2>

        {/* Wide: 3-column table */}
        <div className="hidden md:block max-w-5xl mx-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border">
                {data.col_labels.map((label) => (
                  <th
                    key={label}
                    className={cn(
                      T.metadata,
                      'text-left py-3 px-4 uppercase tracking-wider font-semibold',
                    )}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <motion.tr
                  key={row.task}
                  className="border-b border-border"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
                >
                  <td className={cn(T.body, 'py-4 px-4 font-medium text-foreground')}>
                    {row.task}
                  </td>
                  <td className={cn(T.body, 'py-4 px-4 text-muted-foreground')}>
                    {row.old_way}
                  </td>
                  <td className={cn(T.body, 'py-4 px-4 font-medium text-primary')}>
                    {row.klyde}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Narrow: stacked mini-cards */}
        <div className="md:hidden max-w-md mx-auto space-y-4">
          {data.rows.map((row, i) => (
            <motion.div
              key={row.task}
              className="rounded-xl border border-border bg-card/50 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
            >
              <p className={cn(T.metadata, 'uppercase tracking-wider font-semibold mb-2')}>
                {row.task}
              </p>
              <p className={cn(T.body, 'text-muted-foreground mb-2')}>{row.old_way}</p>
              <p className={cn(T.body, 'font-medium text-primary')}>{row.klyde}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
